import { track } from "@amplitude/analytics-react-native";
import { useApolloClient } from "@apollo/client";
import { toast } from "burnt";
import { useCallback } from "react";
import HidePhotoDepictedInDocument from "src/api/graphql/__generated__/documents/HidePhotoDepictedInDocument";
import RemoveOwnTagFromPhotoDocument from "src/api/graphql/__generated__/documents/RemoveOwnTagFromPhotoDocument";
import RemoveOwnTagFromPhotoWithTagsDocument from "src/api/graphql/__generated__/documents/RemoveOwnTagFromPhotoWithTagsDocument";
import ModerationEvaluation from "src/api/graphql/__generated__/enums/ModerationEvaluation";
import evictPhotos from "src/api/graphql/evictPhotos";
import { useGatheringStore } from "src/contexts/GatheringContext";
import { useClientId } from "src/helpers/globalHelpers";
import log from "src/helpers/log";
import toastError from "src/helpers/toastError";
import usePromptDeletePhoto from "src/hooks/usePromptDeletePhoto";
import usePromptReportPhoto from "src/hooks/usePromptReportPhoto";
import useSetGatheringPhotoModeration from "src/hooks/useSetGatheringPhotoModeration";
import { useInsideModal } from "src/modals";
import { alert } from "src/swsh-native";
import usePhotoDetailStore from "../stores/usePhotoDetailStore";

const handleNotFound = () => {
	log.error("Photo not found");
	toastError();
};

const usePhotoOptionsActions = () => {
	const { closeSelf } = useInsideModal();
	const { promptReportPhoto } = usePromptReportPhoto();
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const viewMode = useGatheringStore((state) => state.viewMode);
	const setViewMode = useGatheringStore((state) => state.setViewMode);
	const apolloClient = useApolloClient();
	const clientId = useClientId();

	const handleReport = useCallback(async () => {
		const photo = usePhotoDetailStore.getState().curPhoto;
		if (!photo) {
			handleNotFound();
			return;
		}
		if (photo.ownerId === clientId) {
			throw new Error("User tried to report own photo");
		}

		await promptReportPhoto(photo.photoId);
	}, [promptReportPhoto, clientId]);

	const { setGatheringPhotoModeration } = useSetGatheringPhotoModeration();
	const handleHide = useCallback(async () => {
		const photo = usePhotoDetailStore.getState().curPhoto;
		if (!photo) {
			handleNotFound();
			return;
		}

		alert({
			title: "Are you sure you want to hide this content?",
			message: "This will hide the content from album participants.",
			buttons: [
				{
					text: "Cancel",
					style: "cancel",
				},
				{
					text: "Hide",
					style: "default",
					onPress: async () => {
						track("photo_hide_confirm");
						// Closing first for better UX
						closeSelf();
						await setGatheringPhotoModeration({
							gatheringId,
							photoIds: [photo.photoId],
							moderation: ModerationEvaluation.ManualBlock,
						});
					},
				},
			],
		});
	}, [gatheringId, closeSelf, setGatheringPhotoModeration]);

	const handleUnhide = useCallback(async () => {
		const photo = usePhotoDetailStore.getState().curPhoto;
		if (!photo) {
			handleNotFound();
			return;
		}

		alert({
			title: "Are you sure you want to unhide this content?",
			message: "This will make the content visible to album participants.",
			buttons: [
				{
					text: "Cancel",
					style: "cancel",
				},
				{
					text: "Unhide",
					style: "default",
					onPress: async () => {
						track("photo_unhide_confirm");
						// Closing first for better UX
						closeSelf();
						await setGatheringPhotoModeration({
							gatheringId,
							photoIds: [photo.photoId],
							moderation: ModerationEvaluation.ManualAllow,
						});
					},
				},
			],
		});
	}, [gatheringId, closeSelf, setGatheringPhotoModeration]);

	const handleHidePhotoDepictedIn = useCallback(async () => {
		const photo = usePhotoDetailStore.getState().curPhoto;
		if (!photo) {
			handleNotFound();
			return;
		}
		const photoId = photo.photoId;
		track("photo_hideDepicted_initiate");
		alert({
			title: "Are you sure?",
			message:
				"This content of you will be hidden from other participants. Hosts can manually unhide.",
			buttons: [
				{
					text: "Cancel",
					style: "cancel",
					onPress: () => {
						track("photo_hideDepicted_cancel");
					},
				},
				{
					text: "Hide",
					style: "destructive",
					onPress: async () => {
						track("photo_hideDepicted_confirm");
						await apolloClient.mutate({
							mutation: HidePhotoDepictedInDocument,
							variables: {
								input: {
									photoId,
								},
							},
						});
						evictPhotos(apolloClient.cache, [photoId], {
							delay: 100,
						});
						closeSelf();
					},
				},
			],
		});
	}, [apolloClient, closeSelf]);

	const handleNotMe = useCallback(async () => {
		const { curPhoto: photo, listenerTopic } = usePhotoDetailStore.getState();
		if (!photo) {
			handleNotFound();
			return;
		}
		track("photo_notMe_initiate");
		alert({
			title: "This isn't me",
			message: "Make sure your profile picture is up to date to find all your pics!",
			buttons: [
				{
					text: "Cancel",
					style: "cancel",
					onPress: () => {
						track("photo_notMe_cancel");
					},
				},
				{
					text: "Untag me",
					style: "destructive",
					onPress: async () => {
						track("photo_notMe_confirm");
						if (viewMode === "Filter") {
							/**
							 * If a user is filtering for themselves and untags, then the photo will no longer be
							 * available in the current filter view so we switch view mode to 'All'. We also use
							 * a query that does _not_ return tags to prevent race conditions with `useGatheringPhotosWithLocal`
							 * which triggers when toggling to 'All' photos.
							 */
							await apolloClient.mutate({
								mutation: RemoveOwnTagFromPhotoDocument,
								variables: {
									input: { photoId: photo.photoId },
								},
							});
							setViewMode("All");
						} else {
							await apolloClient.mutate({
								mutation: RemoveOwnTagFromPhotoWithTagsDocument,
								variables: {
									input: { photoId: photo.photoId },
								},
							});
						}
						listenerTopic.emit("refreshAsyncSettledPhoto", {});
						toast({ preset: "done", title: "Success", message: "Tag removed" });
					},
				},
			],
		});
	}, [apolloClient, viewMode, setViewMode]);

	const { promptDeletePhoto } = usePromptDeletePhoto();
	const handleDelete = useCallback(async () => {
		const photo = usePhotoDetailStore.getState().curPhoto;
		if (!photo) {
			handleNotFound();
			return;
		}
		if (photo.ownerId !== clientId) {
			log.error("User tried to delete someone else's photo");
			toastError();
			return;
		}

		await promptDeletePhoto(photo.photoId, {
			onDelete: () => {
				closeSelf();
			},
		});
	}, [promptDeletePhoto, closeSelf, clientId]);

	return {
		handleReport,
		handleHide,
		handleUnhide,
		handleHidePhotoDepictedIn,
		handleDelete,
		handleNotMe,
	};
};
export default usePhotoOptionsActions;
